// Particle effect functions

import type p5 from 'p5';
import {
    particles,
    getScreenShake,
    setScreenShake,
    getSelectedShip,
    getDt,
    getBoosterParticles,
    setBoosterParticles
} from '../../store/index';
import type { Enemy, BoosterParticle, Particle } from '../../types/game-types';
import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';

/**
 * createBossDefeatEffect() - ボス撃破時の大爆発エフェクト
 * 4段階の派手なエフェクトを生成
 */
export function createBossDefeatEffect(boss: Enemy): void {
    const p = getP5();
    // 強烈な画面揺れ
    setScreenShake(25);

    // 第1段階: 中心からの大爆発
    for (let i = 0; i < 40; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(3, 12);
        particles.push({
            x: boss.x,
            y: boss.y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(5, 20),
            color: p.color(255, p.random(50, 150), 0),
            life: p.random(40, 80)
        });
    }

    // 第2段階: 連続爆発（時間差）
    for (let k = 0; k < 8; k++) {
        setTimeout(() => {
            const offsetX = p.random(-50, 50);
            const offsetY = p.random(-50, 50);

            // 爆発パーティクル
            for (let i = 0; i < 20; i++) {
                const angle = p.random(p.TWO_PI);
                const speed = p.random(2, 8);
                particles.push({
                    x: boss.x + offsetX,
                    y: boss.y + offsetY,
                    vx: p.cos(angle) * speed,
                    vy: p.sin(angle) * speed,
                    size: p.random(4, 15),
                    color: p.color(
                        p.random(200, 255),
                        p.random(100, 255),
                        p.random(0, 100)
                    ),
                    life: p.random(30, 60)
                });
            }

            // 追加の画面揺れ
            const shake = getScreenShake();
            setScreenShake(p.max(shake, 15 - k * 2));
        }, k * 80);
    }

    // 第3段階: 光の輪エフェクト用パーティクル
    for (let i = 0; i < 30; i++) {
        const angle = (p.TWO_PI / 30) * i;
        particles.push({
            x: boss.x,
            y: boss.y,
            vx: p.cos(angle) * 8,
            vy: p.sin(angle) * 8,
            size: p.random(8, 15),
            color: p.color(255, 255, 200),
            life: p.random(30, 50)
        });
    }

    // 第4段階: 星型のキラキラ
    for (let i = 0; i < 20; i++) {
        setTimeout(() => {
            const angle = p.random(p.TWO_PI);
            const dist = p.random(20, 80);
            particles.push({
                x: boss.x + p.cos(angle) * dist,
                y: boss.y + p.sin(angle) * dist,
                vx: p.random(-1, 1),
                vy: p.random(-3, -1),
                size: p.random(3, 8),
                color: p.color(255, 255, 0),
                life: p.random(40, 70)
            });
        }, p.random(100, 500));
    }
}

/**
 * createArmorDeflectEffect() - 装甲敵が弾を弾いた時のエフェクト
 */
export function createArmorDeflectEffect(x: number, y: number, _enemy: Enemy): void {
    const p = getP5();
    // 火花エフェクト
    for (let i = 0; i < 10; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(3, 7);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(2, 6),
            color: p.color(255, 255, 200), // 黄色い火花
            life: p.random(10, 25)
        });
    }

    // 衝撃波リング
    for (let i = 0; i < 8; i++) {
        const angle = (p.TWO_PI / 8) * i;
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * 5,
            vy: p.sin(angle) * 5,
            size: p.random(3, 5),
            color: p.color(180, 180, 220), // シルバー
            life: p.random(15, 25)
        });
    }

    // 小さな画面揺れ
    const shake = getScreenShake();
    setScreenShake(p.max(shake, 3));
}

/**
 * createPlayerDeathEffect() - 自機被弾時の爆発エフェクト
 * 機体カラーを使用した迫力のある多段階爆発
 */
export function createPlayerDeathEffect(x: number, y: number): void {
    const p = getP5();
    const selectedShip = getSelectedShip();
    const shipColor = CONFIG.ships[selectedShip].color;
    const r = shipColor[0], g = shipColor[1], b = shipColor[2];

    // 1. 中央フラッシュ（白→機体色に膨張して消える）
    const flashLife = 18;
    particles.push({
        x, y, vx: 0, vy: 0,
        size: 60,
        color: p.color(255, 255, 255),
        life: flashLife, maxLife: flashLife,
        expand: true, expandRate: 1.06,
        eraseDelay: 0.4
    });

    // 2. 機体色の二次フラッシュ
    const flash2Life = 25;
    particles.push({
        x, y, vx: 0, vy: -0.2,
        size: 45,
        color: p.color(r, g, b),
        life: flash2Life, maxLife: flash2Life,
        expand: true, expandRate: 1.05,
        eraseDelay: 0.3
    });

    // 3. メインバースト（機体色 + 白のミックス）
    for (let i = 0; i < 30; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(5, 14);
        const burstLife = p.random(35, 65);
        const isWhite = i < 8;
        particles.push({
            x, y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(6, 16),
            color: isWhite
                ? p.color(255, 255, 255)
                : p.color(r, p.random(g * 0.7, g), p.random(b * 0.7, b)),
            life: burstLife, maxLife: burstLife
        });
    }

    // 4. 高速衝撃波リング
    for (let i = 0; i < 20; i++) {
        const angle = (p.TWO_PI / 20) * i;
        const ringLife = p.random(18, 30);
        particles.push({
            x, y,
            vx: p.cos(angle) * 12,
            vy: p.sin(angle) * 12,
            size: p.random(5, 10),
            color: p.color(r, g, b),
            life: ringLife, maxLife: ringLife
        });
    }

    // 5. エネルギースパーク（高速で短寿命）
    for (let i = 0; i < 15; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(8, 18);
        const sparkLife = p.random(12, 22);
        particles.push({
            x, y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(3, 7),
            color: p.color(255, 255, p.random(200, 255)),
            life: sparkLife, maxLife: sparkLife
        });
    }

    // 6. 連鎖爆発（時間差で周囲に小爆発）
    for (let k = 0; k < 4; k++) {
        setTimeout(() => {
            const offsetAngle = p.random(p.TWO_PI);
            const offsetDist = p.random(15, 35);
            const ox = x + p.cos(offsetAngle) * offsetDist;
            const oy = y + p.sin(offsetAngle) * offsetDist;

            for (let i = 0; i < 8; i++) {
                const angle = p.random(p.TWO_PI);
                const speed = p.random(3, 8);
                const debrisLife = p.random(25, 45);
                particles.push({
                    x: ox, y: oy,
                    vx: p.cos(angle) * speed,
                    vy: p.sin(angle) * speed,
                    size: p.random(4, 12),
                    color: p.color(
                        p.random(r * 0.5, r),
                        p.random(g * 0.5, g),
                        p.random(b * 0.5, b)
                    ),
                    life: debrisLife, maxLife: debrisLife
                });
            }
        }, k * 60);
    }

    // 7. 残煙（暗い色、長寿命、上方向へ）
    for (let i = 0; i < 6; i++) {
        const smokeLife = p.random(60, 90);
        particles.push({
            x: x + p.random(-10, 10),
            y: y + p.random(-5, 5),
            vx: p.random(-0.5, 0.5),
            vy: p.random(-1.5, -0.3),
            size: p.random(20, 35),
            color: p.color(60, 50, 45),
            life: smokeLife, maxLife: smokeLife
        });
    }
}

/**
 * createExplosion() - 爆発エフェクトの生成
 * 敵撃破時やゲームオーバー時に呼ばれる
 */
export function createExplosion(x: number, y: number, col: p5.Color): void {
    const p = getP5();
    setScreenShake(4);  // 画面を揺らす（控えめ）

    // 20個のパーティクルを放射状に生成
    for (let i = 0; i < 20; i++) {
        const angle = p.random(p.TWO_PI);     // ランダムな方向
        const speed = p.random(2, 8);        // ランダムな速度
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,      // X方向の速度
            vy: p.sin(angle) * speed,      // Y方向の速度
            size: p.random(3, 10),
            color: col,
            life: p.random(20, 40)         // 寿命（フレーム数）
        });
    }
}

/**
 * createHitParticles() - ヒットエフェクトの生成
 * 弾が敵に当たった時の小さなエフェクト
 */
export function createHitParticles(x: number, y: number): void {
    const p = getP5();
    for (let i = 0; i < 5; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(1, 3);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(2, 5),
            color: p.color(255, 255, 255),  // 白
            life: p.random(10, 20)
        });
    }
}

/**
 * createLaserPenetrateEffect() - レーザー貫通エフェクト
 * レーザーが敵を貫通した時の簡易エフェクト
 */
export function createLaserPenetrateEffect(x: number, y: number): void {
    const p = getP5();
    // 紫のスパーク
    for (let i = 0; i < 4; i++) {
        const angle = p.random(-p.PI / 4, p.PI / 4) - p.HALF_PI; // 上方向に広がる
        const speed = p.random(2, 4);
        const sparkLife = p.random(8, 15);
        particles.push({
            x: x + p.random(-5, 5),
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(3, 6),
            color: p.color(255, 150, 255),
            life: sparkLife,
            maxLife: sparkLife
        });
    }
    // 中心の閃光
    const flashLife = 6;
    particles.push({
        x: x,
        y: y,
        vx: 0,
        vy: 0,
        size: 10,
        color: p.color(255, 200, 255),
        life: flashLife,
        maxLife: flashLife
    });
}

/**
 * createHomingHitEffect() - ホーミング弾命中エフェクト
 * ミサイル着弾時の迫力ある爆発エフェクト
 */
export function createHomingHitEffect(x: number, y: number): void {
    const p = getP5();

    // 画面揺れ（ミサイル着弾らしく強め）
    const shake = getScreenShake();
    setScreenShake(p.max(shake, 10));

    // 1. 中心の白い閃光（大きく明るく）
    const flashLife = 20;
    particles.push({
        x: x,
        y: y,
        vx: 0,
        vy: 0,
        size: 50,
        color: p.color(255, 255, 255),
        life: flashLife,
        maxLife: flashLife
    });

    // 2. 黄色い二次閃光
    const flash2Life = 25;
    particles.push({
        x: x,
        y: y,
        vx: 0,
        vy: 0,
        size: 40,
        color: p.color(255, 255, 100),
        life: flash2Life,
        maxLife: flash2Life
    });

    // 3. 火球の膨張（周囲のオレンジ火球）- 小さく始まって広がって消える
    for (let i = 0; i < 3; i++) {
        const fireballLife = p.random(18, 28);
        const offset = p.random(8, 15);
        const angle = p.random(p.TWO_PI);
        particles.push({
            x: x + p.cos(angle) * offset,
            y: y + p.sin(angle) * offset,
            vx: p.random(-0.8, 0.8),
            vy: p.random(-1.5, 0.3),
            size: p.random(8, 12),
            color: p.color(255, p.random(80, 150), 0),
            life: fireballLife,
            maxLife: fireballLife,
            expand: true,
            expandRate: 1.04,
            targetX: p.random(-5, 5),
            targetY: p.random(-5, 5),
            eraseAngle: p.random(p.TWO_PI)
        });
    }

    // 4. 赤い内核火球 - 小さく始まって広がって消える
    for (let i = 0; i < 2; i++) {
        const coreLife = p.random(20, 30);
        particles.push({
            x: x + p.random(-4, 4),
            y: y + p.random(-4, 4),
            vx: p.random(-0.4, 0.4),
            vy: p.random(-0.8, 0),
            size: p.random(10, 14),
            color: p.color(255, p.random(30, 80), 0),
            life: coreLife,
            maxLife: coreLife,
            expand: true,
            expandRate: 1.04,
            targetX: p.random(-5, 5),
            targetY: p.random(-5, 5),
            eraseAngle: p.random(p.TWO_PI)
        });
    }

    // 5. 衝撃波リング - 狭く早く消える
    for (let i = 0; i < 8; i++) {
        const angle = (p.TWO_PI / 8) * i;
        const speed = p.random(3, 5);
        const ringLife = p.random(15, 25);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(8, 14),
            color: p.color(255, 255, 200),
            life: ringLife,
            maxLife: ringLife
        });
    }

    // 6. 二次衝撃波 - 狭く早く消える
    for (let i = 0; i < 6; i++) {
        const angle = (p.TWO_PI / 6) * i + p.PI / 6;
        const speed = p.random(2, 4);
        const ring2Life = p.random(18, 30);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(6, 10),
            color: p.color(255, 200, 100),
            life: ring2Life,
            maxLife: ring2Life
        });
    }

    // 7. 爆発の破片 - 狭く早く消える
    for (let i = 0; i < 5; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(1.5, 3);
        const debrisLife = p.random(20, 35);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed - 0.5,
            size: p.random(12, 22),
            color: p.color(255, p.random(100, 200), p.random(0, 50)),
            life: debrisLife,
            maxLife: debrisLife
        });
    }

    // 8. 火花 - 狭く早く消える
    for (let i = 0; i < 5; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(2, 5);
        const sparkLife = p.random(15, 25);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(5, 10),
            color: p.color(255, 255, p.random(150, 255)),
            life: sparkLife,
            maxLife: sparkLife
        });
    }

    // 9. 黒煙 - 少なく大きく
    for (let i = 0; i < 4; i++) {
        const smokeLife = p.random(70, 100);
        particles.push({
            x: x + p.random(-12, 12),
            y: y + p.random(-6, 6),
            vx: p.random(-0.6, 0.6),
            vy: p.random(-2, -0.5),
            size: p.random(28, 45),
            color: p.color(60, 50, 40),
            life: smokeLife,
            maxLife: smokeLife
        });
    }

    // 10. 灰色の残煙 - 少なく大きく
    for (let i = 0; i < 3; i++) {
        const trailLife = p.random(90, 130);
        particles.push({
            x: x + p.random(-10, 10),
            y: y + p.random(-5, 5),
            vx: p.random(-0.3, 0.3),
            vy: p.random(-1.2, -0.3),
            size: p.random(22, 35),
            color: p.color(100, 90, 85),
            life: trailLife,
            maxLife: trailLife
        });
    }

    // 11. 中央の大火球（1つ）- 最前面に描画、小さく始まって広がって消える
    const centerFireLife = 30;
    particles.push({
        x: x,
        y: y,
        vx: 0,
        vy: -0.3,
        size: 18,
        color: p.color(255, 230, 200),
        life: centerFireLife,
        maxLife: centerFireLife,
        expand: true,
        expandRate: 1.04,
        eraseDelay: 0.5,
        targetX: p.random(-8, 8),
        targetY: p.random(-8, 8),
        eraseAngle: p.random(p.TWO_PI)
    });
}

/**
 * createCollectParticles() - アイテム取得エフェクト
 * アイテムを取得した時の派手なエフェクト
 */
export function createCollectParticles(x: number, y: number, col: p5.Color): void {
    const p = getP5();
    for (let i = 0; i < 15; i++) {
        const angle = p.random(p.TWO_PI);
        const speed = p.random(2, 6);
        particles.push({
            x: x,
            y: y,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed - 2,  // 上向きの力を追加
            size: p.random(3, 8),
            color: col,
            life: p.random(15, 30)
        });
    }
}

/**
 * createBoosterEffect() - ブースターエフェクトの生成
 * 自機のエンジン炎エフェクト
 */
export function createBoosterEffect(x: number, y: number, size: number, alpha: number): void {
    const p = getP5();
    if (alpha <= 5) {
        return;
    }

    const boosterIntensity = alpha / 255;
    const particleCount = p.floor(6 * boosterIntensity);

    // 機体の色を取得
    const selectedShip = getSelectedShip();
    const shipCfg = CONFIG.ships[selectedShip];
    const shipColor = shipCfg.color;

    // Get current booster particles array
    const boosterParticles = getBoosterParticles();

    // メインエンジン炎（機体の色に合わせる）
    for (let i = 0; i < particleCount; i++) {
        const spreadX = p.random(-12, 12) * boosterIntensity;
        boosterParticles.push({
            x: x + spreadX,
            y: y + size / 2 + p.random(0, 15),
            vx: spreadX * 0.3,
            vy: p.random(8, 18),
            size: p.random(8, 22) * boosterIntensity,
            color: p.color(shipColor[0], shipColor[1], shipColor[2], 200 * boosterIntensity),
            life: p.random(15, 30),
            gravity: 0.3,
            alpha: 200 * boosterIntensity
        });
    }

    // 白い中心
    if (boosterIntensity > 0.3) {
        for (let i = 0; i < p.floor(2 * boosterIntensity); i++) {
            boosterParticles.push({
                x: x + p.random(-4, 4),
                y: y + size / 2 + 5,
                vx: p.random(-1, 1),
                vy: p.random(6, 12),
                size: p.random(6, 12) * boosterIntensity,
                color: p.color(255, 255, 255, 220 * boosterIntensity),
                life: p.random(12, 22),
                gravity: 0.2,
                alpha: 220 * boosterIntensity
            });
        }
    }

    // サイドバーニア（機体の色に合わせる）
    if (boosterIntensity > 0.5) {
        for (let side = -1; side <= 1; side += 2) {
            boosterParticles.push({
                x: x + side * 16,
                y: y + size / 2,
                vx: side * p.random(2, 6),
                vy: p.random(4, 10),
                size: p.random(4, 9) * boosterIntensity,
                color: p.color(shipColor[0], shipColor[1], shipColor[2], 180 * boosterIntensity),
                life: p.random(12, 20),
                gravity: 0.15,
                alpha: 180 * boosterIntensity
            });
        }
    }

    // 煙エフェクト（長く尾を引く）
    if (p.frameCount % 2 === 0 && boosterIntensity > 0.4) {
        boosterParticles.push({
            x: x + p.random(-8, 8),
            y: y + size / 2 + 25,
            vx: p.random(-1, 1),
            vy: p.random(1, 4),
            size: p.random(12, 25) * boosterIntensity,
            color: p.color(150, 150, 150, 100 * boosterIntensity),
            life: p.random(30, 50),
            gravity: 0.05,
            alpha: 100 * boosterIntensity
        });
    }

    // Update the booster particles array
    setBoosterParticles(boosterParticles);
}

/**
 * updateParticleArray() - パーティクル配列の共通更新処理
 */
export function updateParticleArray(arr: (Particle | BoosterParticle)[]): void {
    const p = getP5();
    const dt = getDt();
    for (let i = arr.length - 1; i >= 0; i--) {
        arr[i].x += arr[i].vx * dt;
        arr[i].y += arr[i].vy * dt;
        const gravity = (arr[i] as BoosterParticle).gravity !== undefined ? (arr[i] as BoosterParticle).gravity : 0.1;
        arr[i].vy += (gravity as number) * dt;
        arr[i].life -= dt;
        // expand: trueなら広がって消える、falseならしぼむ
        if ((arr[i] as Particle).expand) {
            const rate = (arr[i] as Particle).expandRate || 1.01;
            arr[i].size *= p.pow(rate, dt);
        } else {
            arr[i].size *= p.pow(0.95, dt);
        }
        if (arr[i].life <= 0) {
            arr.splice(i, 1);
        }
    }
}
